import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import Navigation from "../Navigation/Navigation";
import HomeFooter from "../HomePage/HomeFooter";

import CardsIcon from "../../Assets/Icons/cards.png";
import LoaderBox from "../HomePage/LoaderBox";
import HomeSponsors from "../HomePage/HomeSponsors";
import BottomNavigation from "../Navigation/BottomNavigation";

export default function BelaTurnir() {
  const { turnirSlug } = useParams();

  const [matches, setMatches] = useState([]);
  const [turnirInfo, setTurnirInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(32);
  const turnirScrollRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [currentTurnirId, setCurrentTurnirId] = useState(null);

  const getDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const fetchBracket = useCallback(async () => {
    if (!turnirSlug) {
      setLoading(false);
      return;
    }

    try {
      // 1. Dohvat info o turniru iz tablice "admin_turniri"
      const { data: turnirData, error: turnirError } = await supabase
        .from("admin_turniri")
        .select("*")
        .eq("slug", turnirSlug)
        .single();

      if (turnirError || !turnirData) {
        console.error("Greška pri dohvatu info o turniru:", turnirError);
        setLoading(false);
        return;
      }
      setCurrentTurnirId(turnirData.id);
      setTurnirInfo(turnirData);
      const currentTurnirId = turnirData.id;

      // 2. Dohvat bela mečeva bez fiksog sortiranja po nepostojećem stupcu
      const { data, error } = await supabase
        .from("turniri_bela")
        .select("*")
        .eq("turnir_id", currentTurnirId);

      if (error) {
        console.error("Greška pri dohvatu bela mečeva:", error);
      } else {
        setMatches(data || []);

        if (data && data.length > 0) {
          // Provjeravamo koji stupac možda koristš za slotove (bracket_slot_id, slot_id ili sl.)
          const maxSlot = Math.max(
            ...data.map((m) => m.bracket_slot_id ?? m.slot_id ?? 0),
          );
          if (maxSlot === 63) setTeamCount(64);
          else if (maxSlot === 31) setTeamCount(32);
          else if (maxSlot === 15) setTeamCount(16);
          else if (maxSlot === 7) setTeamCount(8);
          else if (maxSlot === 3) setTeamCount(4);
        }
      }
    } catch (err) {
      console.error("Neočekivana greška:", err);
    } finally {
      setLoading(false);
    }
  }, [turnirSlug]);

  useEffect(() => {
    const container = turnirScrollRef.current;
    if (!container) return;

    let startDist = 0;
    let startZoom = zoom;
    let startCenter = { x: 0, y: 0 };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        startCenter = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        };
        startDist = getDistance(e.touches);
        startZoom = zoom;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && startDist > 0) {
        if (e.cancelable) e.preventDefault();

        const currentDist = getDistance(e.touches);
        const factor = currentDist / startDist;

        const nextZoom = Math.max(0.4, Math.min(startZoom * factor, 1.5));

        if (Math.abs(nextZoom - zoom) > 0.001) {
          const currentScrollLeft = container.scrollLeft;
          const currentScrollTop = container.scrollTop;
          const zoomRatio = nextZoom / zoom;

          setZoom(nextZoom);

          container.scrollLeft =
            (currentScrollLeft + startCenter.x) * zoomRatio - startCenter.x;
          container.scrollTop =
            (currentScrollTop + startCenter.y) * zoomRatio - startCenter.y;
        }
      }
    };

    const onTouchEnd = () => {
      startDist = 0;
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
    };
  }, [zoom]);

  useEffect(() => {
    fetchBracket();

    if (!turnirInfo?.id) return;

    const subscription = supabase
      .channel(`turniri_bela_${turnirInfo.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "turniri_bela",
          filter: `turnir_id=eq.${turnirInfo.id}`,
        },
        () => {
          fetchBracket();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [turnirInfo?.id, fetchBracket]);

  const finalSlotId = teamCount - 1;
  const totalStages = Math.floor(Math.log2(teamCount));

  const getMatchBySlot = (slotId) => {
    // Podržavamo traženje po bracket_slot_id ili slot_id
    const match = matches.find(
      (m) => (m.bracket_slot_id ?? m.slot_id) === slotId,
    );
    return (
      match || {
        team_a_name: "?",
        team_b_name: "?",
        scorea: 0,
        scoreb: 0,
        table_number: null,
        match_time: null,
        is_completed: false,
      }
    );
  };

  const getWinner = () => {
    const finale = getMatchBySlot(finalSlotId);
    if (!finale || !finale.is_completed) return "?";
    if (Number(finale.scorea) > Number(finale.scoreb))
      return finale.team_a_name;
    if (Number(finale.scoreb) > Number(finale.scorea))
      return finale.team_b_name;
    return "?";
  };

  const renderMatchCard = (slotId, sideClass) => {
    const m = getMatchBySlot(slotId);
    const imaInfo = m.table_number || m.match_time;
    return (
      <div key={slotId} className={`bela-match ${sideClass}`}>
        <div className="team">
          <h4>{m.team_a_name || "?"}</h4>
          <span>{m.scorea}</span>
        </div>
        <div className="team">
          <h4>{m.team_b_name || "?"}</h4>
          <span>{m.scoreb}</span>
        </div>{" "}
        {imaInfo && (
          <h5 className="match-info">
            {m.table_number ? `STOL ${m.table_number}` : ""}
            {m.table_number && m.match_time ? " - " : ""}
            {m.match_time ? m.match_time : ""}
          </h5>
        )}
        <div className="vertical-line"></div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navigation
          turnirSlug={turnirSlug}
          turnirNaziv={turnirInfo?.naziv}
          turnirLogo={turnirInfo?.logo_url}
          lokacija={turnirInfo?.lokacija}
          nazivLokacije={turnirInfo?.naziv_lokacije}
          features={turnirInfo?.features}
        />
        <div className="bela-turnir-wrapper">
          <LoaderBox text="Učitavanje bela turnira..." />
        </div>
        <BottomNavigation />
      </>
    );
  }

  const leftSidesColumns = [];
  const rightSidesColumns = [];

  let currentOffset = 0;
  for (let stage = totalStages; stage > 1; stage--) {
    const matchesInStageCount = Math.pow(2, stage - 1);
    const halfCount = matchesInStageCount / 2;

    const leftSlots = [];
    const rightSlots = [];

    for (let i = 1; i <= halfCount; i++) {
      leftSlots.push(currentOffset + i);
    }
    for (let i = 1; i <= halfCount; i++) {
      rightSlots.push(currentOffset + halfCount + i);
    }

    leftSidesColumns.push({ stageClass: `stage-${stage}`, slots: leftSlots });
    rightSidesColumns.unshift({
      stageClass: `stage-${stage}`,
      slots: rightSlots,
    });

    currentOffset += matchesInStageCount;
  }

  return (
    <>
      <Navigation
        turnirSlug={turnirSlug}
        turnirNaziv={turnirInfo?.naziv || "Bela Turnir"}
        turnirLogo={turnirInfo?.logo}
        lokacija={turnirInfo?.lokacija}
        nazivLokacije={turnirInfo?.naziv_lokacije || turnirInfo?.lokacija}
        features={turnirInfo?.features}
      />
      <div className="bela-turnir-wrapper">
        <div className="championship-stage-header">
          <img src={CardsIcon} alt="Stage" className="stage-img" />
          <h3>Bela turnir</h3>
        </div>
        <div className="zoom-slider-container">
          <button
            type="button"
            className="zoom-reset-btn"
            onClick={() => setZoom(1)}
            title="Resetiraj na 100%"
          >
            {Math.round(zoom * 100)}%
          </button>
          <span className="zoom-label">
            <i className="fi fi-rs-zoom-in"></i>
          </span>
          <div className="slider-wrapper">
            <input
              type="range"
              min="0.4"
              max="1.5"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="zoom-vertical-slider"
            />
          </div>
          <span className="zoom-label">
            <i className="fi fi-rs-zoom-out"></i>
          </span>
        </div>
        <div className="bela-turnir-scroll" ref={turnirScrollRef}>
          <div
            className="bela-turnir"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "left center",
              transition: "transform 0.05s ease-out",
              width: "max-content",
              height: "max-content",
              maxWidth: `calc(100% * ${zoom})`,
              display: "flex",
              alignItems: "center",
              gap: "0px",
            }}
          >
            <div className="left-side">
              {leftSidesColumns.map((col, index) => (
                <div
                  key={`left-col-${index}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-left"))}
                </div>
              ))}
            </div>

            <div className="middle-side">
              <div className="final-content-wrapper">
                <div className="bela-match finale-box">
                  {(() => {
                    const m = getMatchBySlot(finalSlotId);
                    const imaFinalInfo = m.table_number || m.match_time;
                    return (
                      <>
                        <div className="team">
                          <h4>{m.team_a_name || "?"}</h4>
                          <span>{m.scorea}</span>
                        </div>
                        <div className="team">
                          <h4>{m.team_b_name || "?"}</h4>
                          <span>{m.scoreb}</span>
                        </div>

                        {imaFinalInfo && (
                          <h5 className="match-info-final">
                            {m.table_number ? `STOL ${m.table_number}` : ""}
                            {m.table_number && m.match_time ? " - " : ""}
                            {m.match_time ? m.match_time : ""}
                          </h5>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="trophy-container">
                  <img src={CardsIcon} alt="Trophy" className="stage-img" />
                  <h3>POBJEDNIK</h3>
                  <div className="winner-box">{getWinner()}</div>
                </div>
              </div>
            </div>

            <div className="right-side">
              {rightSidesColumns.map((col, index) => (
                <div
                  key={`right-col-${index}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-right"))}
                </div>
              ))}
            </div>
            <div style={{ minWidth: "30px", height: "1px", flexShrink: 0 }} />
          </div>
        </div>

        <div className="scroll-hint">
          <div className="line"></div>
          <span>Povuci za faze</span>
          <div className="line"></div>
        </div>
      </div>
      <HomeSponsors turnirId={currentTurnirId} /> <BottomNavigation />
      <HomeFooter />
    </>
  );
}
